import { zodResolver } from "@hookform/resolvers/zod";
import {
  type Advisor,
  clientQueryKeys,
  useCreateClientDataConsent,
  useUpdateOneClient,
} from "@verde/entities";
import { WhatsappIcon } from "@verde/icons";
import { useAuthContext } from "@verde/modules";
import { Button, FormControl, InputMask, Label } from "@verde/ui-core";
import { queryClient, z } from "@verde/utils";
import React, { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAuthorizationDialog } from "./authorization-dialog";

const schema = z.object({
  phone_number: z.string().min(11, "Telefone inválido"),
});
type Schema = z.infer<typeof schema>;

function AuthorizationDialogWhatsapp() {
  const { client, onClose } = useAuthorizationDialog();
  const { user } = useAuthContext<Advisor>();

  const create = useCreateClientDataConsent(client?.id);
  const updateClient = useUpdateOneClient(client?.id, {
    hideSuccessFeedback: true,
  });
  const loading = updateClient.isLoading || create.isLoading;

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    setError,
  } = useForm<Schema>({
    resolver: zodResolver(schema),
  });

  const phoneNumberValue = watch("phone_number");

  const phoneNumberError = useMemo(() => {
    return (phoneNumberValue || client?.phone_number) === user?.mobile;
  }, [phoneNumberValue, client, user]);

  const handleOnSubmit = useCallback(
    async ({ phone_number }: Schema) => {
      if (phoneNumberError) return;

      await updateClient.mutateAsync({
        phone_number,
      });

      await create.mutateAsync({
        kind: "scr",
        channel: "whatsapp",
        has_data_consent: true,
      });

      await queryClient.invalidateQueries(
        clientQueryKeys.detail.data_consent(client?.id),
      );

      onClose?.();
    },
    [phoneNumberError, create, updateClient, client, onClose],
  );

  useEffect(() => {
    if (client?.phone_number) {
      setValue("phone_number", client.phone_number);
    }
  }, [client, setValue]);

  useEffect(() => {
    if (phoneNumberError) {
      setError("phone_number", {
        message: "Utilize o telefone de cliente para solicitar a assinatura",
      });
    }
  }, [phoneNumberError, setError]);

  return (
    <form className="grid gap-4" onSubmit={handleSubmit(handleOnSubmit)}>
      <p className="text-base font-medium text-brand-green-dark">
        Antes de enviar, verifique o contato o contato do produtor.
      </p>

      <div className="grid gap-4 rounded border border-neutral-light-2 p-4">
        <FormControl
          id="phone_number"
          label="Enviando para:"
          kind={errors.phone_number && "error"}
          helper={errors.phone_number?.message}
        >
          <Controller
            control={control}
            name="phone_number"
            render={({ field }) => (
              <InputMask.Root
                {...field}
                options={InputMask.Options.Cellphone}
                autoComplete="off"
                autoFocus
                placeholder="(00) 0 0000-0000"
              />
            )}
          />
        </FormControl>

        <div className="grid gap-2 rounded border border-neutral-light-2 p-4">
          <Label>Mensagem que será enviada</Label>

          <div className="grid gap-4 rounded bg-neutral-light-1 p-4 text-base font-medium">
            Olá, {client?.name}. Aqui é o time da Verde Crédito Digital. O
            Assessor {user?.name}, quer consultar os detalhes do seu CPF em
            nossa plataforma para ajudar você a tomar crédito. Você autoriza a
            consulta?
          </div>
        </div>
      </div>

      <hr />

      <Button
        type="submit"
        size="lg"
        icon={<WhatsappIcon />}
        loading={loading}
        disabled={!phoneNumberValue}
      >
        Enviar link para produtor
      </Button>
    </form>
  );
}

export default React.memo(AuthorizationDialogWhatsapp);
